import { useMemoizedFn, useSetState } from 'ahooks';
import { useEffect, useRef } from 'react';

/** Sticky 状态接口 */
interface StickyState {
  /** 是否处于 sticky 状态 */
  isSticky: boolean;
  /** sticky 触发的偏移量，初始为 null，等待首次滚动时计算 */
  stickyOffset: number | null;
}

/** useSticky hook 参数接口 */
interface UseStickyProps {
  /** 滚动容器的id */
  scrollEleId: string;
}

/**
 * 滚动容器 sticky 状态管理 Hook
 * @param props - Hook 参数
 * @returns sticky 状态和控制方法
 */
export const useSticky = ({ scrollEleId }: UseStickyProps) => {
  // 存储元素的引用
  const stickyEleRef = useRef<HTMLDivElement | null>(null);
  // 存储 sticky 状态和触发位置
  const [stickyState, setStickyState] = useSetState<StickyState>({
    isSticky: false,
    stickyOffset: null,
  });

  const handleScroll = useMemoizedFn(() => {
    const element = stickyEleRef.current;
    if (!element) return;

    const elementOffsetTop = element.offsetTop;
    if (!stickyState.stickyOffset && elementOffsetTop !== stickyState.stickyOffset) {
      setStickyState({
        isSticky: false,
        stickyOffset: elementOffsetTop,
      });
      return;
    }
    const isSticky = stickyState.stickyOffset ? elementOffsetTop > stickyState.stickyOffset : false;
    if (isSticky !== stickyState.isSticky) {
      setStickyState({
        isSticky,
      });
    }
  });

  useEffect(() => {
    const scrollEle = document.getElementById(scrollEleId);
    if (!scrollEle) return;
    // 添加滚动监听
    scrollEle.addEventListener('scroll', handleScroll);

    return () => {
      scrollEle.removeEventListener('scroll', handleScroll);
    };
  }, [scrollEleId, handleScroll]);

  const scrollToTop = useMemoizedFn(() => {
    const scrollEle = document.getElementById(scrollEleId);
    if (!scrollEle || !stickyState.isSticky || !stickyState.stickyOffset) return;

    scrollEle.scrollTo({
      top: stickyState.stickyOffset + 0.5,
    });
  });

  // 重置 sticky 状态的方法
  const resetSticky = useMemoizedFn(() => {
    setStickyState({
      isSticky: false,
      stickyOffset: null,
    });
  });

  return {
    isSticky: stickyState.isSticky,
    scrollToTop,
    stickyEleRef,
    resetSticky,
  };
};
